<template>
  <v-container v-if="species" :key="species.id">
    <v-row>
      <v-col>
        <div class="text-h4">{{ species.name_sve }}</div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-carousel
          height="auto"
          hide-delimiter-background
          :show-arrows-on-hover="true"
          :show-arrows="images.length > 1"
          :class="currentlyShowingVideo ? 'livet-carousel-showing-video' : ''"
          @change="onCarouselChange"
        >
          <v-carousel-item eager v-for="image in images" :key="image.id" cycle>
            <div class="video-iframe-container" v-if="image.videoUrl">
              <iframe
                class="video-iframe"
                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; autoplay"
                allowfullscreen
                :src="patchedVideoUrl"
              ></iframe>
            </div>
            <template v-else>
              <v-img contain :src="$_getImage(image.id)" />
              <div class="attribution">{{ image.copyright }}</div>
            </template>
          </v-carousel-item>
        </v-carousel>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <!-- <span class="subtitle-1 text-uppercase color-primary">{{
          species.name_lat
        }}</span> -->
        <div class="text-h5">{{ species.name_sve }}</div>
      </v-col>
    </v-row>
    <v-row>
      <v-col :cols="$vuetify.breakpoint.mdAndUp ? 8 : 12">
        <p v-html="species.text_sve"></p>
        <OLMap v-if="isOnline" :species="species" class="mb-8" />
        <v-img v-if="isOffline" :src="fallbackImageUrl" class="mb-8" />
        <!-- <button class="mb-5" @click="downloadMapAsImage(species)">
          LADDA NER KARTBILD (ta bort knapp sen)
        </button> -->
        <p v-html="species.text_env_sve"></p>
        <SpeciesFactBox :species="species" />
        <!-- <div v-if="isOffline || $_clientMode === 'kiosk'">
          <h3 class="my-5 text--secondary">Liknande arter</h3>
          <SingleSpeciesSimilar :species="species" />
        </div> -->
      </v-col>
      <v-col :cols="$vuetify.breakpoint.mdAndUp ? 4 : 12">
        <SpeciesFacts :species="species" />
      </v-col>
    </v-row>
    <v-row>
      <v-col :cols="$vuetify.breakpoint.smAndDown ? 12 : 9" class="mb-5">
        <div class="text--secondary" v-if="updatedAt">
          Sidan uppdaterad {{ updatedAt }}
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-divider></v-divider>
      </v-col>
    </v-row>
    <v-row
      v-if="!$_kioskMode"
      :class="$vuetify.breakpoint.smAndDown ? 'flex-column-reverse' : ''"
    >
      <v-col cols="12" class="col-md-8">
        <h3 class="mb-5 text--secondary">Du kanske också är intresserad av</h3>
        <SingleSpeciesSimilar
          :species="species"
          :count="$vuetify.breakpoint.mdAndDown ? 6 : 6"
        />
        <v-btn
          class="mt-10"
          color="primary"
          @click="routeTo(species.category_url)"
          text
          ripple
        >
          <v-icon left>
            mdi-arrow-left
          </v-icon>
          Tillbaka till {{ species.category }}
        </v-btn>
      </v-col>

      <v-col cols="12" class=" col-md-4">
        <SingleSpeciesLinks :species="species" />
      </v-col>
    </v-row>

    <v-row v-if="$_kioskMode">
      <v-col cols="12">
        <h3 class="mb-5 text--secondary">Du kanske också är intresserad av</h3>
        <SingleSpeciesSimilar
          :species="species"
          :count="$vuetify.breakpoint.mdAndDown ? 6 : 8"
        />
        <v-btn
          class="mt-10"
          color="primary"
          @click="routeTo(species.category_url)"
          text
          ripple
        >
          <v-icon left>
            mdi-arrow-left
          </v-icon>
          Tillbaka till {{ species.category }}
        </v-btn>
      </v-col>
    </v-row>

    <SingleSpeciesNews v-if="isOnline" :species="species" />
    <SingleSpeciesHUartiklar v-if="isOnline" :species="species" />
    <SingleSpeciesAvhandlingar v-if="isOnline" :species="species" />
  </v-container>
</template>

<script>
/* eslint-disable no-unused-vars */
import DataLayer from '@/store/SingleSpeciesDataLayer'
import Setup from '@/mixins/Setup.js'
import Misc from '@/mixins/Misc.js'
import OLMap from '@/components/Map'
import SpeciesFacts from '@/components/SpeciesFacts'
import SingleSpeciesLinks from '@/components/SingleSpeciesLinks'
import SingleSpeciesNews from '@/components/SingleSpeciesNews'
import SingleSpeciesAvhandlingar from '@/components/SingleSpeciesAvhandlingar'
import SingleSpeciesHUartiklar from '@/components/SingleSpeciesHUartiklar'
import SpeciesFactBox from '@/components/SpeciesFactBox'
import SingleSpeciesSimilar from '@/components/SingleSpeciesSimilar'
import { add } from 'date-fns'

export default {
  name: 'SingleSpecies',
  mixins: [Setup, Misc],
  components: {
    OLMap,
    SpeciesFacts,
    SingleSpeciesLinks,
    SingleSpeciesNews,
    SingleSpeciesAvhandlingar,
    SingleSpeciesHUartiklar,
    SpeciesFactBox,
    SingleSpeciesSimilar
  },
  props: {
    nameUrl: String
  },
  data() {
    return {
      species: null,
      images: [],
      carouselIndex: null
    }
  },
  watch: {
    $route: 'setup'
  },
  updated() {
    document.title = this.species.name_sve
  },
  computed: {
    fallbackImageUrl() {
      return `${process.env.VUE_APP_IMAGE_BACKEND}/livet-pwa/static-maps/${this.species.id}.png`
    },
    updatedAt() {
      return 'updated_at' in this.species && this.species.updated_at
        ? this.species.updated_at.substring(0, 10)
        : null
    },

    currentlyShowingVideo() {
      if (this.carouselIndex === null) {
        return false
      }
      return !!this.images[this.carouselIndex].videoUrl
    },

    patchedVideoUrl() {
      const videoUrl = this.images[this.carouselIndex]?.videoUrl
      return videoUrl ? videoUrl + '?autoplay=1&loop=1' : videoUrl
    }
  },
  methods: {
    onCarouselChange(index) {
      this.carouselIndex = index
    },
    downloadMapAsImage(species) {
      let downloadLink = document.createElement('a')
      downloadLink.setAttribute('download', species.id + '.png')
      let canvas = document.querySelector('canvas')
      canvas.toBlob(function(blob) {
        let url = URL.createObjectURL(blob)
        downloadLink.setAttribute('href', url)
        downloadLink.click()
      })
    },
    routeTo(nameUrl) {
      this.$router.push({
        name: 'SpeciesCategory',
        params: { categoryNameUrl: nameUrl }
      })
    },
    async setup() {
      this.images = []
      const res = this.$store.state.allSpecies
      const species = res.find(species => species.url === this.nameUrl)
      if (!species) {
        this.$router.push({ name: '404' })
      } else if (species && this.isOnline) {
        this.images.push({
          id: species.li_image_id,
          copyright: species.image_copyright,
          videoUrl: null
        })
        const additionalImages =
          (await this.getAdditionalImages(species.id)) || []
        additionalImages.forEach(additionalImage => {
          if (this.images.find(i => i.id === additionalImage.id)) {
            return
          }
          this.images.push(additionalImage)
        })
      } else {
        // If offline
        this.images.push({
          id: species.li_image_id,
          copyright: species.image_copyright,
          videoUrl: null
        })
      }

      this.species = species
    },

    async getAdditionalImages(speciesId) {
      const { data } = await DataLayer.GetAdditionalImages(speciesId)
      const res = data.map(i => {
        return i.video_url
          ? { id: i.id, videoUrl: i.video_url }
          : { id: i.image, copyright: i.image_copyright }
      })
      return res
    }
  }
}
</script>

<style lang="css" scoped>
.subtitle-1 {
  color: #7ead61;
}
.attribution {
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: #fff;
  color: #000;
  font-size: 12px;
  padding-left: 30px;
  padding-right: 5px;
}
.video-iframe-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */
  margin-bottom: 10px;
  z-index: 2;
}
.video-iframe-container .video-iframe {
  border: 0;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
}
</style>
